// Accordion item for a single field
// Shows preview when collapsed, full editor when expanded
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { captureException } from '@startus/lib';
import { useTranslation } from 'react-i18next';
import { useAccordion } from './StartupProfileForm';
import { useFieldMetadata } from '../context/FieldMetadataContext';
import { useStartupForm } from '../context/StartupFormContext';
import { useFormMode } from '../context/FormModeContext';
import { getFieldComponent } from '../config/fieldRegistry';
import { CountryMultiselectField } from './fields/CountryMultiselectField';
import { TextAreaField } from './fields/TextAreaField';
import { FieldStatusDropdown } from './FieldStatusDropdown';
import { AIComparisonPanel } from './AIComparisonPanel';
import { CharacterCounter } from './CharacterCounter';
import type { FieldConfig, FieldOption } from '../types/fields';
import type { ReactNode } from 'react';

interface FieldAccordionItemProps {
  field: FieldConfig;
  sectionId: string;
  fieldIndex: number;
  onAIRegenerate?: (fieldName: string, currentValue: string) => void;
  // Whether the AI agent can regenerate (disables button when false)
  canAIRegenerate?: boolean;
  // Override options (e.g. dynamically resolved cities based on selected countries)
  resolvedOptions?: FieldConfig['options'];
  // Opt-in: always render the expanded editor, skipping the collapse/expand accordion gate —
  // default (omitted) keeps the existing click-to-expand row. Used for "title + textbox always
  // visible" layouts (e.g. one-liner fields) instead of the collapsed label/preview pattern.
  alwaysExpanded?: boolean;
  // Opt-in: render with TextAreaField instead of the field's registry component — a purely
  // visual "bigger textbox" swap for text fields, default (omitted) uses the registry lookup.
  renderAsTextarea?: boolean;
  // Opt-in: custom node for the collapsed-row preview (e.g. tag pills) instead of the default
  // comma-joined text — only used when the field has a value, so the empty "Click to enter"
  // state is unaffected. Default (omitted) keeps the existing text preview everywhere else.
  renderCollapsedPreview?: (value: unknown, options?: FieldOption[]) => ReactNode;
  // Opt-in (OPA-307 round 11): collapsed row as a dashboard-style tile — label on top, value
  // below — instead of the default label-left/value-right row. Only changes the collapsed
  // (view-mode) presentation; the expanded editor is untouched. Default (omitted) keeps the
  // existing row layout everywhere else.
  stackedPreview?: boolean;
  // Opt-in: a multiselect field whose options only ever have one valid choice renders as a
  // single-select dropdown (MultiselectField's singleSelect mode) instead of the toggle-pill
  // row — a purely visual swap, the field's value stays a 1-item array either way. No-op for
  // non-multiselect field types. Default (omitted) keeps the existing multi-pick behavior.
  singleSelectMultiselect?: boolean;
}

// Get initials from full name (e.g. "Yerlin Sibaja" → "YS")
export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join('');
}

// Relative time from ISO string (e.g. "hace 2 horas")
export function timeAgo(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) return 'hace unos segundos';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `hace ${diffHr}h`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `hace ${diffDays}d`;
  const diffMonths = Math.floor(diffDays / 30);
  return `hace ${diffMonths} mes${diffMonths > 1 ? 'es' : ''}`;
}

// Get display value for preview — resolves option labels for select/multiselect fields.
// truncate defaults to true (compact row/tile previews); callers with room for the full
// value (e.g. a textarea tile spanning the full row width) pass false so long text wraps
// instead of getting cut mid-word.
export function getPreviewValue(
  value: unknown,
  options?: { value: string; label: string }[],
  truncate = true
): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    // Resolve labels if options available, otherwise show count
    if (options?.length) {
      const labels = value.map((v) => options.find((o) => o.value === v)?.label ?? v);
      const joined = labels.join(', ');
      return truncate && joined.length > 50 ? `${joined.slice(0, 50)}...` : joined;
    }
    return `${value.length} item(s)`;
  }
  // Resolve single-select label if options available
  if (options?.length) {
    const match = options.find((o) => o.value === String(value));
    if (match) return match.label;
  }
  const str = String(value);
  return truncate && str.length > 50 ? `${str.slice(0, 50)}...` : str;
}

// Status dot colors aligned with backend enum
const STATUS_DOT_COLORS: Record<string, string> = {
  in_progress: 'bg-[#FFDD53]',
  ai_review: 'bg-[#42BEFC]',
  marked_for_review: 'bg-[#EF7700]',
  validated: 'bg-[#20BD5A]',
};

export function FieldAccordionItem({
  field,
  sectionId,
  fieldIndex: _fieldIndex,
  onAIRegenerate,
  canAIRegenerate = true,
  resolvedOptions,
  alwaysExpanded = false,
  renderAsTextarea = false,
  renderCollapsedPreview,
  stackedPreview = false,
  singleSelectMultiselect = false,
}: FieldAccordionItemProps) {
  const {
    activeField,
    openField,
    closeField,
    toggleSection,
    firstSectionId,
    isEditingAll,
    isGlobalEditFormMode,
  } = useAccordion();
  const { getMetadata } = useFieldMetadata();
  const { onFieldHidden } = useStartupForm();
  const { isReadOnly } = useFormMode();
  const { watch, getValues, setValue } = useFormContext();
  const { t } = useTranslation('common');

  // Evaluate backend requirements: show field only when all conditions match (AND logic)
  const requirementIds = field.requirements?.map((r) => r.questionId) ?? [];
  const watchedValues = watch(requirementIds);
  const isHiddenByRequirements =
    field.requirements != null &&
    field.requirements.length > 0 &&
    !field.requirements.every((req, i) => watchedValues[i] === req.optionValue);

  // When hidden: clear form value and delete BE record if a saved valueId exists
  useEffect(() => {
    if (!isHiddenByRequirements) return;
    setValue(field.name, undefined, { shouldDirty: false });
    const valueId = getMetadata(field.name).valueId;
    if (valueId && onFieldHidden) {
      onFieldHidden(field.name, valueId).catch((err) => captureException(err));
    }
  }, [isHiddenByRequirements, field.name, setValue, getMetadata, onFieldHidden]);

  if (isHiddenByRequirements) return null;

  const isActive =
    alwaysExpanded ||
    isEditingAll ||
    (activeField?.sectionId === sectionId && activeField?.fieldName === field.name);
  const metadata = getMetadata(field.name);
  const currentValue = watch(field.name);

  const handleClick = () => {
    // Global edit mode: editing is gated by the page-level Edit toggle, not per-field
    // clicks — in view mode there's nothing to open, in edit mode every field is already
    // expanded (isActive above), so a click on the collapsed row is a no-op either way.
    if (isGlobalEditFormMode) return;
    if (isActive) {
      closeField();
    } else {
      openField(sectionId, field.name);
    }
  };

  const handleAIRegenerate = () => {
    if (onAIRegenerate && field.aiEnabled) {
      const value = getValues(field.name);
      onAIRegenerate(field.name, String(value || ''));
    }
  };

  // Get the field component from registry (or force textarea for a taller-box visual variant)
  const FieldComponent = renderAsTextarea ? TextAreaField : getFieldComponent(field.type);

  return (
    <div
      data-field-name={field.name}
      className={`field-accordion-item h-full ${isActive ? 'bg-white/6' : 'bg-[#1a1a1a]'}`}
    >
      {/* Collapsed header — two layouts: default row (dot + label left, preview right), or
          the dashboard-tile "stacked" layout (label on top, value below) used by view-mode
          dashboard sections (OPA-307 round 11). */}
      {!alwaysExpanded && !isActive && stackedPreview && (
        <div className="px-3.5 py-3 min-w-0">
          {/* No `truncate` on the label — cutting off the question text mid-word was the bug
              (Briam, OPA-348 2026-09-10); it wraps instead, and SectionFieldsGrid/
              StartupProfilePage keep long questions to a 1/2-width tile so it usually stays
              short anyway. */}
          <div className="flex items-start gap-2 min-w-0 mb-1.5">
            <span
              className={`w-2 h-2 rounded-full shrink-0 mt-[3px] ${STATUS_DOT_COLORS[metadata.status] || 'bg-gray-600'}`}
            />
            <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">
              {field.label}
            </span>
          </div>
          {(() => {
            const opts = resolvedOptions ?? field.options;
            // Every stackedPreview tile (SectionFieldsGrid / StartupProfilePage dashboard
            // grids) sizes itself to the value — no hard character cutoff here either way
            // (Briam, OPA-346 for one-liners, generalized to all stacked tiles OPA-348
            // 2026-09-10: "que la respuesta use todo el campo existente... para ver el
            // contenido exacto"). whitespace-pre-line wraps instead of clipping.
            const preview = getPreviewValue(currentValue, opts, false);
            const isEmpty = preview === '—';
            if (!isEmpty && renderCollapsedPreview) {
              return (
                <div className="flex flex-wrap gap-1.5">
                  {renderCollapsedPreview(currentValue, opts)}
                </div>
              );
            }
            return (
              <span
                className={`text-sm whitespace-pre-line ${isEmpty ? 'text-gray-500' : 'text-white'}`}
              >
                {isEmpty ? t('common:common.clickToEnter') : preview}
              </span>
            );
          })()}
        </div>
      )}
      {!alwaysExpanded && !isActive && !stackedPreview && (
        <div
          onClick={handleClick}
          className={`flex items-center justify-between px-5 py-3.5 transition-colors ${
            isGlobalEditFormMode ? '' : 'cursor-pointer hover:bg-white/5'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={`w-3 h-3 rounded-full shrink-0 ${STATUS_DOT_COLORS[metadata.status] || 'bg-gray-600'}`}
            />
            <span className="text-sm font-medium text-white">{field.label}</span>
          </div>
          {(() => {
            const opts = resolvedOptions ?? field.options;
            const preview = getPreviewValue(currentValue, opts);
            const isEmpty = preview === '—';
            if (!isEmpty && renderCollapsedPreview) {
              return (
                <div className="ml-4 flex flex-wrap justify-end gap-1.5 max-w-[280px]">
                  {renderCollapsedPreview(currentValue, opts)}
                </div>
              );
            }
            return (
              <span
                className={`text-sm truncate ml-4 max-w-[200px] text-right ${isEmpty ? 'text-gray-500' : 'text-[#42BEFC]'}`}
                title={!isEmpty && preview.endsWith('...') ? String(currentValue) : undefined}
              >
                {isEmpty ? t('common:common.clickToEnter') : preview}
              </span>
            );
          })()}
        </div>
      )}

      {/* Expanded content - Figma: dropdown > label > input */}
      {isActive && (
        <div className="px-5 py-4 space-y-3">
          {/* Question row: label only — status dropdown moved to controls row below */}
          <label className="text-sm font-medium text-white">{field.label}</label>

          {/* Help text — always directly below the question. Fixed min-height when
              alwaysExpanded+renderAsTextarea (one-liner cards) so sibling cards with
              shorter help text don't push their textarea up, leaving uneven card contents. */}
          {field.helpText && (
            <p
              className={`text-sm text-base-content/60 ${alwaysExpanded && renderAsTextarea ? 'min-h-[2.5rem]' : ''}`}
            >
              {field.helpText}
            </p>
          )}

          {/* Field editor */}
          <div onClick={(e) => e.stopPropagation()}>
            {field.optionsSource === 'countries' ? (
              <CountryMultiselectField
                name={field.name}
                label=""
                options={resolvedOptions ?? field.options}
              />
            ) : (
              <FieldComponent
                name={field.name}
                label=""
                placeholder={
                  field.placeholder ||
                  (['select', 'multiselectDropdown', 'multiselect'].includes(field.type)
                    ? t('common:common.select')
                    : t('common:common.typeHere'))
                }
                required={field.required}
                options={resolvedOptions ?? field.options}
                itemFields={field.itemFields}
                addButtonLabel={field.addButtonLabel}
                itemLabelPrefix={field.itemLabelPrefix}
                maxLength={field.maxLength}
                accept={field.accept}
                maxFiles={field.maxFiles}
                singleSelect={field.type === 'multiselect' ? singleSelectMultiselect : undefined}
              />
            )}
          </div>

          {/* Metadata row - edit info left, char count right */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            {metadata.updatedAt ? (
              <div className="flex items-center gap-2">
                {/* Avatar: image or initials circle */}
                {metadata.editedByName &&
                  (metadata.editedByAvatar ? (
                    <img
                      src={metadata.editedByAvatar}
                      alt={metadata.editedByName}
                      className="w-5 h-5 rounded-full border-2 border-white object-cover"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[#19A2F8] border-2 border-white flex items-center justify-center text-[9px] font-bold text-white">
                      {getInitials(metadata.editedByName)}
                    </div>
                  ))}
                <span>
                  {metadata.editedByName
                    ? `${metadata.editedByName} | `
                    : `${t('common:ai.edited')} | `}
                  {timeAgo(metadata.updatedAt)}
                </span>
              </div>
            ) : (
              <span />
            )}

            {/* Character counter */}
            {field.maxLength && (
              <CharacterCounter
                count={
                  Array.isArray(currentValue)
                    ? currentValue.length
                    : String(currentValue || '').length
                }
                maxLength={field.maxLength}
              />
            )}
          </div>

          {/* AI comparison panel (when suggestion available) */}
          {metadata.aiSuggestion && (
            <AIComparisonPanel fieldName={field.name} currentValue={String(currentValue || '')} />
          )}

          {/* Controls row: both buttons share full width (50/50), or status alone at w-1/2.
              Reformulate is edit-only: on globalEditToggle pages isReadOnly is always false
              (mode is hardcoded to 'edit', the dashboard's own isEditingAll flag is what
              actually distinguishes its view/edit states), so it also has to gate on
              isEditingAll there or the button leaks into the dashboard's view mode. Consumers
              without globalEditToggle keep the original isReadOnly-only behavior. */}
          {field.aiEnabled && !isReadOnly && (!isGlobalEditFormMode || isEditingAll) ? (
            <div className="flex items-center gap-3 w-full min-w-0">
              <button
                type="button"
                onClick={handleAIRegenerate}
                disabled={metadata.isAIGenerating || !canAIRegenerate}
                className="btn btn-sm btn-startus-gradient rounded-full flex-1 min-w-0 gap-2 px-1 md:px-4"
              >
                {metadata.isAIGenerating ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    {t('common:ai.generating')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {t('common:ai.reformulate')}
                  </>
                )}
              </button>
              <FieldStatusDropdown fieldName={field.name} className="flex-1" />
            </div>
          ) : (
            // Solo status dropdown: fixed at half container width, left-aligned
            <FieldStatusDropdown fieldName={field.name} className="w-1/2" />
          )}

          {/* Hint: above DaisyUI dropdown-content (z-20) via z-[30] */}
          {field.aiEnabled &&
            !isReadOnly &&
            (!isGlobalEditFormMode || isEditingAll) &&
            !canAIRegenerate &&
            sectionId !== firstSectionId && (
              <div className="relative z-[30] text-center">
                <button
                  type="button"
                  onClick={() => {
                    toggleSection(firstSectionId);
                    // Scroll <main> to top since first section is at the top
                    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-xs text-white/40 underline hover:text-white/70 transition-colors"
                >
                  {t('common:ai.completeBasicData')}
                </button>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
